import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import authService from '../../services/authService'
import keycloakAuth from '../../services/keycloakAuth'

const initialState = {
  token: authService.getAuthToken(),
  user: authService.getUserInfo(),
  loading: false,
  error: null,
  logoutInProgress: false,
}

export const logout = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    const idToken = authService.getIdToken()
    authService.clearAuth()
    keycloakAuth.logout(idToken)

    // This return will never be reached because keycloakAuth.logout() redirects
    // But we keep it for type safety
    return null
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const verifyToken = createAsyncThunk('auth/verifyToken', async (_, { rejectWithValue }) => {
  try {
    const user = await authService.getCurrentUser()
    return user
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCredentials: (state, action) => {
      const { token, refreshToken, idToken, user } = action.payload
      state.token = token
      state.user = user
      authService.setSession({ token, refreshToken, idToken, user })
    },
    clearCredentials: (state) => {
      state.token = null
      state.user = null
      state.logoutInProgress = false
      authService.clearAuth()
    },
    markLogoutComplete: (state) => {
      state.logoutInProgress = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout.pending, (state) => {
        state.loading = true
        state.logoutInProgress = true
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false
        state.token = null
        state.user = null
        state.error = null
        // keep logoutInProgress true until logged-out page confirms redirect
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.logoutInProgress = false
      })
      .addCase(verifyToken.pending, (state) => {
        state.loading = true
      })
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload
        authService.setUserInfo(action.payload)
      })
      .addCase(verifyToken.rejected, (state) => {
        state.loading = false
        state.token = null
        state.user = null
        state.logoutInProgress = false
        authService.clearAuth()
      })
  },
})

export const { clearError, setCredentials, clearCredentials, markLogoutComplete } = authSlice.actions
export default authSlice.reducer
